<template>
  <div class="action-card" :class="`is-${state}`">
    <div class="action-header">
      <b-icon icon="pencil-outline" size="is-small" class="action-icon" />
      <strong class="action-title">{{ action.title }}</strong>
      <span v-if="recordLabel" class="action-record">
        <a
          v-if="safeUrl"
          :href="safeUrl"
          target="_blank"
          rel="noopener noreferrer"
          >{{ recordLabel }}</a
        >
        <span v-else>{{ recordLabel }}</span>
      </span>
    </div>

    <dl v-if="changes.length" class="action-changes">
      <div
        v-for="(change, index) in changes"
        :key="`${change.field}-${index}`"
        class="action-change"
      >
        <dt>{{ change.field }}</dt>
        <dd v-if="'before' in change" class="action-before">
          <span class="action-label">{{ $t('chat_box.action_before') }}</span>
          <span class="action-value">{{
            change.before || $t('chat_box.action_empty')
          }}</span>
        </dd>
        <dd class="action-after">
          <span v-if="'before' in change" class="action-label">{{
            $t('chat_box.action_after')
          }}</span>
          <span class="action-value">{{
            change.after || $t('chat_box.action_empty')
          }}</span>
        </dd>
      </div>
      <div v-if="action.hiddenChanges" class="action-more">
        {{ $t('chat_box.action_more_fields', { count: action.hiddenChanges }) }}
      </div>
    </dl>

    <div v-if="state === 'pending'" class="action-buttons">
      <b-button
        size="is-small"
        type="is-primary"
        :loading="busy === 'apply'"
        :disabled="disabled || !!busy"
        @click="$emit('apply', action)"
        >{{ $t('chat_box.action_apply') }}</b-button
      >
      <b-button
        size="is-small"
        :loading="busy === 'cancel'"
        :disabled="disabled || !!busy"
        @click="$emit('cancel', action)"
        >{{ $t('chat_box.action_cancel') }}</b-button
      >
    </div>
    <div v-else class="action-status">
      <b-icon :icon="statusIcon" size="is-small" />
      <span>{{ $t(`chat_box.action_status_${state}`) }}</span>
    </div>
  </div>
</template>

<script>
// Proposed DIJ changes from the chat agent: nothing changes in DIJ until the
// user applies one here. Statuses come from the backend (see actionResult).
import { FINAL_STATES } from '../actionState';

const STATUS_ICONS = {
  applied: 'check-circle-outline',
  cancelled: 'close-circle-outline',
};

export default {
  props: {
    action: {
      type: Object,
      required: true,
    },
    // 'apply' or 'cancel' while that request is in flight.
    busy: {
      type: String,
      default: null,
    },
    // Another request is in flight.
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { now: Date.now() };
  },
  computed: {
    state() {
      if (FINAL_STATES.includes(this.action.status)) return this.action.status;
      if (
        !this.busy &&
        this.action.expiresAt &&
        this.now / 1000 >= this.action.expiresAt
      ) {
        return 'expired';
      }
      return 'pending';
    },
    changes() {
      return Array.isArray(this.action.changes) ? this.action.changes : [];
    },
    recordLabel() {
      const record = this.action.record || {};
      return record.name || record.id || '';
    },
    safeUrl() {
      const url = (this.action.record || {}).url || '';
      return url.startsWith('https://') ? url : '';
    },
    statusIcon() {
      return STATUS_ICONS[this.state] || 'alert-circle-outline';
    },
  },
  mounted() {
    // Lets a pending card show it has expired without waiting for a click.
    this.timer = setInterval(() => {
      this.now = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
};
</script>

<style scoped>
.action-card {
  margin: 0 0 10px 46px;
  max-width: calc(95% - 46px);
  padding: 10px 12px;
  border: 1px solid #c3cdf5;
  border-left: 4px solid #5a67d8;
  border-radius: 8px;
  background-color: #fff;
  color: #333;
  font-size: 0.9rem;
}

.action-card.is-applied {
  border-left-color: #48c78e;
}

.action-card.is-cancelled,
.action-card.is-expired,
.action-card.is-unavailable {
  border-left-color: #b5b5b5;
}

.action-card.is-failed,
.action-card.is-conflict,
.action-card.is-refused {
  border-left-color: #f14668;
}

.action-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
}

.action-icon {
  color: #5a67d8;
}

.action-record {
  min-width: 0;
  overflow-wrap: anywhere;
  color: #555;
}

/* The chat bubbles' link colours; the app styles all links !important. */
.action-record a {
  color: #1f4aa8 !important;
  text-decoration: underline !important;
}

.action-record a:hover {
  color: #0d2f7a !important;
}

.action-changes {
  margin: 8px 0 0;
}

.action-change {
  margin-bottom: 6px;
}

.action-change dt {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.action-change dd {
  display: flex;
  gap: 6px;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.action-label {
  flex-shrink: 0;
  color: #777;
}

.action-before .action-value {
  color: #777;
  text-decoration: line-through;
}

.action-more {
  color: #777;
  font-style: italic;
}

.action-buttons {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.action-status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  color: #555;
}
</style>
